import {Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {ActivatedRoute, NavigationEnd, NavigationStart, Router} from '@angular/router';
import {Subject, Subscription, timer} from 'rxjs';
import {filter, takeUntil} from 'rxjs/operators';
import scrollIntoView from 'scroll-into-view-if-needed';

import {JASO} from '../shared/defines/gukeo.const';
import {
  BookmarkChange,
  HISTORY_KEY_SEARCH_PROVERB,
  LIST_TYPE,
  SearchParam,
} from '../shared/defines/client.const';
import {GetSearchFromQuery} from "../shared/defines/client.func";
import {MENU_PAGE, PROVERB_PAGE_SIZE} from '../shared/defines/desktop.const';
import {PageLoader} from '../shared/interfaces/page-loader';
import {Proverb} from '../shared/models/proverb.model';
import {VoiceService} from '../shared/services/voice/voice.service';
import {OptionService} from '../shared/services/option/option.service';
import {ProverbService} from '../shared/services/proverb/proverb.service';
import {ProverbBookmarkService} from '../shared/services/proverb/proverb-bookmark.service';
import {ProverbHistoryService} from '../shared/services/proverb/proverb-history.service';
import {GlobalEventService} from "../shared/services/global-event/global-event.service";

@Component({
  selector: 'app-proverb',
  templateUrl: './proverb.component.html',
  styleUrls: ['./proverb.component.scss']
})
export class ProverbComponent implements OnInit, OnDestroy {
  // @ViewChild('container') container: ElementRef | undefined;
  // @ViewChild('reload') reload: ElementRef | undefined;
  @ViewChild('main', { static: true }) main?: ElementRef; // ngOnInit() 에서 사용 가능
  @ViewChildren('proverbItem', {read: ElementRef}) proverbItems?: QueryList<ElementRef<HTMLElement>>;

  protected readonly PROVERB_PAGE_HALF_SIZE = PROVERB_PAGE_SIZE / 2;

  private type: LIST_TYPE | null = null;

  // 라우터에서 넣어주는 값. (TODO: 초기화에 유의해야 함)
  private queryParams: SearchParam | null  = null;

  // 해더에 넣는 값
  public selectedMenu: MENU_PAGE = MENU_PAGE.PROVERB;
  public curJasoIdx: JASO | null = null;
  public keyword: string | null = null;
  public placeholder: string | null = null;
  readonly HISTORY_KEY_SEARCH_PROVERB = HISTORY_KEY_SEARCH_PROVERB;
  protected scrollTop = true;

  // PageLoader 설정
  public pl: PageLoader<Proverb, string> | null = null;

  // 라우딩 처리
  private routerStop$ = new Subject();

  private queryParams$?: Subscription;
  private route$?: Subscription;

  public get ListLeft(): Array<Proverb> | null {
    return this.listHalfIndex ? (this.pl?.getList()?.slice(0, this.listHalfIndex) ?? null) : null;
  }

  public get ListRight(): Array<Proverb> | null {
    return this.listHalfIndex ? (this.pl?.getList()?.slice(this.listHalfIndex) ?? null) : null;
  }

  protected get IsLoading(): boolean {
    return this.pl?.IsLoading() ?? false;
  }

  // 상세화면 표시 관련 정의
  public showDetail = false;
  public curDetail: Proverb | null = null; // 현재 선택된 단어

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private voice: VoiceService,
    private option: OptionService,
    private proverbSvc: ProverbService,
    private bmSvc: ProverbBookmarkService,
    private historySvc: ProverbHistoryService,
    private globalEventSvc: GlobalEventService,
  ) {
    // super(globalEventSvc);

    /******************************************************************************************
     * 라우터 파리미터 변경 핸들러 - 주소 뒤의 파라미터를 받아오는 곳. ( ?keyword=... )
     * 입력된 파라미터가 없어도 처음 초기화 할 때에도 불려진다.
     * 처음 화면에 들어올 때, 라우터 주소 변견 핸들러보다 더 먼저 불려진다. (이상하지만 그렇다)
     *
     * router.navigate()로 같은 주소를 호출하면 이 코드가 다시 불려진다.
     */
    this.queryParams$ = this.route.queryParams
      .subscribe(params => {
        // console.warn('ProverbComponent::queryParams =', params);
        const prevQueryParams = this.queryParams;
        const nextQueryParams = {...params};
        delete nextQueryParams['serial'];

        const listQueryChanged = this.getListQueryKey(prevQueryParams) !== this.getListQueryKey(nextQueryParams);
        this.queryParams = nextQueryParams;

        if (this.type) {
          if (listQueryChanged) {
            // 타입 변경과 동시에 queryParams가 바뀌면 route.params$에서 init()을 처리한다.
            const routeType = this.route.snapshot.params['type'];
            if (routeType === this.type) {
              this.init();
            }
          }
          else if (!this.IsLoading) {
            this.selectProverbFromQuery();
          }
        }
      });

    /******************************************************************************************
     * 라우터 주소 변경 핸들러 - 라우터 주소 읽어오는 곳.
     * 라우터 주소에 포함된 파라미터가 변경되어도 불려진다. (여기서는 type만 변경되어도 불려짐)
     *
     * queryParams가 바뀐 것(주소뒤의 파리미터가 변경된 경우)으로는 여기가 불려지지 않는다.
     */
    this.route$ = route.params.subscribe(params => {
      // console.warn('ProverbComponent::params =', params);
      this.type = params['type'];

      this.init();
    });

    // lm.Reset();
    this.curDetail = null;
  }


  ngOnInit() {
    // 페이지를 이동하면 소리를 멈추도록 구현
    // TODO: 이 부분은 좀 더 정밀하게 할 필요가 있다. 내 컴포넌트(WordComponent)인 경우에만 처리하도록 수정할 필요가 있다.
    this.router.events.pipe(
      takeUntil(this.routerStop$),
      filter(event => event instanceof NavigationStart || event instanceof NavigationEnd),
    ).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.voice.Stop();
      }
    });

    // if (this.main) {
    //   this.InitFocusMain(this.main);
    // }
    // else {
    //   console.error('ProverbComponent::ngOnInit() main is null');
    // }
  }

  ngOnDestroy(): void {
    // router 이동 체크를 중지함.
    this.routerStop$.next(null);

    this.queryParams$?.unsubscribe();
    this.route$?.unsubscribe();

    // this.ExitFocusMain();

    // 소리 재생 중지.
    // TODO: routerStop$ 처리 시 하고 있는데 또 반복적으로 해야 할까?
    this.voice.Stop();
  }

  public onGoHistory(): void {
    this.type = null; // 생성자의 queryParams 쪽으로 init()가 불려지는 것을 막음.
    this.router.navigate(['/proverb', LIST_TYPE.HISTORY]).then();
  }

  private init(): void {
    // console.warn('ProverbComponent::init()', this.type, this.queryParams);
    let param: SearchParam;

    switch (this.type) {
      case LIST_TYPE.SERVER:
        this.selectedMenu = MENU_PAGE.PROVERB;
        this.placeholder = '속담을 검색 합니다.';
        if (this.queryParams?.word_id) {
          this.pl = this.proverbSvc;
          param = { word_id: this.queryParams.word_id };
          this.curJasoIdx = null;
          this.keyword = null;
        }
        else {
          this.pl = this.proverbSvc;
          [param, this.curJasoIdx, this.keyword] = GetSearchFromQuery(this.queryParams);
        }
        this.ReloadList(param);
        break;
      case LIST_TYPE.BOOKMARK:
        this.selectedMenu = MENU_PAGE.PROVERB_BOOKMARK;
        this.pl = this.bmSvc;
        this.curJasoIdx = null;
        this.placeholder = '속담을 검색 합니다.';
        [param, this.curJasoIdx, this.keyword] = GetSearchFromQuery(this.queryParams);
        this.ReloadList(param);
        break;
      case LIST_TYPE.HISTORY:
        this.selectedMenu = MENU_PAGE.PROVERB_HISTORY;
        this.pl = this.historySvc;
        this.curJasoIdx = null;
        this.placeholder = '속담을 검색 합니다.';
        param = (this.queryParams && 'id' in this.queryParams) ? { id: this.queryParams.id } : {};
        this.ReloadList(param);
        break;
      default:
        console.error('Invalid LIST_TYPE =', this.type);
    }
  }


  /****************************************************************************
   * 단어 목록 로딩 처리.
   ****************************************************************************/
  public onSearch(param: SearchParam): void {
    // console.warn('onSearch() param=', param);

    // 단어 이력보기 화면에서 자소를 선택하면 단어 검색으로 변경한다.
    if (this.pl instanceof ProverbHistoryService && ('index' in param || 'keyword' in param)) {
      console.warn('ProverbComponent::ReloadList:: ll, param', this.pl, param);
      this.router.navigate(['/proverb', LIST_TYPE.SERVER], {
        queryParams: {...param}
      }).then();
      return;
    }

    // 선택된 단어를 지운다.
    if (this.curDetail) {
      this.curDetail = null;
    }

    this.router.navigate(['/proverb', LIST_TYPE.SERVER], {
      queryParams: param,
    }).then();
  }

  protected goNextPage($event: MouseEvent): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    const isLast = this.pl?.IsLastPage() ?? true;
    if (!isLast) {
      const queryParams: SearchParam = {
        ...this.queryParams,
        page: (this.pl?.getCurPage() ?? 0) + 1,
      };
      // Spec: docs/page_common_logic.md > 선택 URL 히스토리와 start/id 보존
      // Page navigation may replace `start`, but it must not remove selection `id`.
      delete queryParams.start;
      console.warn('ProverbComponent::goNextPage() queryParams=', queryParams);
      this.router.navigate(['/proverb', this.type], {queryParams}).then();
    }
  }

  protected goPrePage($event: MouseEvent): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    const isFirst = this.pl?.IsFirstPage() ?? true;
    if (!isFirst) {
      const queryParams: SearchParam = {
        ...this.queryParams,
        page: (this.pl?.getCurPage() ?? 0) - 1,
      };
      // Spec: docs/page_common_logic.md > 선택 URL 히스토리와 start/id 보존
      // Page navigation may replace `start`, but it must not remove selection `id`.
      delete queryParams.start;
      console.warn('ProverbComponent::goPrePage() queryParams=', queryParams);
      this.router.navigate(['/proverb', this.type], {queryParams}).then();
    }
  }

  public ReloadList(param: SearchParam): void {
    this.curDetail = null;

    this.pl?.ResetList(param).subscribe(page => {
      this.pl?.GetNextList().subscribe((list: Array<Proverb>) => {
        console.warn('ProverbComponent::ReloadList:: ll, list', this.pl, list);
        this.selectProverbFromQuery();
      });
    });
  }

  public Unfold($event: MouseEvent, proverb: Proverb): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    this.curDetail = proverb;
    this.historySvc.addHistory(proverb.id);

    // Spec: docs/page_common_logic.md > 선택 URL 히스토리와 start/id 보존
    const queryParams: SearchParam = {
      ...(this.queryParams ?? {}),
      id: proverb.id,
    };
    this.router.navigate(['/proverb', this.type], {queryParams}).then();
  }

  protected IsSelected(proverb: Proverb): boolean {
    return this.curDetail?.id === proverb.id;
  }

  private getListQueryKey(param: SearchParam | null): string {
    return JSON.stringify({
      page: param?.page ?? null,
      index: param?.index ?? null,
      keyword: param?.keyword ?? null,
      word_id: param?.word_id ?? null,
    });
  }

  private selectProverbFromQuery(): void {
    const id = this.queryParams?.id ?? null;
    if (!id) {
      this.curDetail = null;
      return;
    }

    const proverb = this.pl?.getById(id) ?? null;
    if (!proverb) {
      this.curDetail = null;
      console.warn('ProverbComponent::selectProverbFromQuery() getById failed, id=', id);
      return;
    }

    this.curDetail = proverb;
    this.scrollToProverb(proverb.id);
  }

  private scrollToProverb(id: string): void {
    timer(100).subscribe(() => {
      const el = this.proverbItems?.find(item => item.nativeElement.dataset['proverbId'] === id)?.nativeElement;
      if (!el) {
        console.warn('ProverbComponent::scrollToProverb() item not found', id);
        return;
      }

      scrollIntoView(el, {
        behavior: 'smooth',
        block: 'nearest',
        scrollMode: 'if-needed',
      });
    });
  }

  protected onBookmarkChange(bookmarkChange: BookmarkChange): void {
    const id = String(bookmarkChange.id);

    if (this.type === LIST_TYPE.BOOKMARK) {
      // 책갈피 목록에서는 해제만 처리한다.
      // 재추가(bookmark===true)는 이 뷰에서 발생하지 않으므로 별도 처리하지 않는다.
      if (!bookmarkChange.bookmark) {
        this.bmSvc.hideBookmark(id);
        if (this.curDetail?.id === id) {
          this.curDetail = null;
        }
      }
      return;
    }

    if (this.curDetail?.id === id) {
      this.curDetail.bookmark = bookmarkChange.bookmark;
    }
  }

  public GetNextBtnCss(): string {
    return this.pl?.IsLoading() ? 'loading' : this.pl?.IsLastPage() ? '' : 'on';
  }

  public GetPreBtnCss(): string {
    return this.pl?.IsLoading() ? 'loading' : this.pl?.IsFirstPage() ? '' : 'on';
  }

  private get listHalfIndex(): number | null {
    const sizeList = this.pl?.getList()?.length;
    // return sizeList ? GetCeilHalf(sizeList) : null;
    return sizeList ? Math.ceil(PROVERB_PAGE_SIZE / 2) : null;
  }


  protected onScrollTop($event: Event): void {
    const target = $event.target as HTMLElement;

    // console.log('onScrollTop() ', target.scrollTop);
    this.scrollTop = target.scrollTop === 0; // 수직 스크롤 위치
  }


  protected showPageNumber(): boolean {
    const curPage = this.pl?.getCurPage();
    const totalPage = this.pl?.getTotalCount();
    return Boolean(curPage ?? false) && Boolean(totalPage ?? false);
  }

  protected getCurrrentPage(): number | null {
    const curPage = this.pl?.getCurPage();
    // console.warn('getCurrrentPage() curPage=', curPage);
    return curPage ? curPage : null;
  }

  protected getTotalPage() {
    const totalPage = this.pl?.getTotalCount();
    return totalPage ? Math.ceil(totalPage / PROVERB_PAGE_SIZE) : null;
  }
}
